/**
 * Shared run utilities — used by sidebar, Workflows page, and any component
 * that needs to display run status, grouping, or metadata.
 *
 * Extracted to avoid triple-duplication across workflow-history-sidebar.tsx,
 * app-sidebar.tsx, and app/workflows/page.tsx.
 */

import type { components } from "@/lib/api/schema"

export type RunMetadataSummary = components["schemas"]["WorkflowSummaryOut"]

// ===== Group ordering =====
export const GROUP_ORDER = ["Today", "Yesterday", "This Week", "This Month", "Older"] as const

/**
 * Strip backend "not yet set" sentinels from config string fields.
 *
 * The backend uses the literal string "pending" as a sentinel for fields
 * that satisfy schema validation but haven't been filled in by the user
 * yet (app_name, repo_url, source_db_type, source_db_connection — see
 * apps/workflows/api.py:_build_initial_config). Surfacing that sentinel
 * verbatim in form fields makes users delete it before they can type.
 *
 * Use this at every API → form-state read point.
 */
export function stripPendingSentinel(value: unknown): string {
  if (typeof value !== "string") return ""
  return value === "pending" ? "" : value
}

// ===== Effective status =====

/**
 * Determine the effective display status for a run.
 *
 * Prefers the backend-provided `effective_status` field (authoritative).
 * Falls back to legacy inference for older API responses that lack it.
 */
export function getEffectiveStatus(run: RunMetadataSummary & { effective_status?: string }): string {
  // Backend-provided authoritative status (preferred)
  if (run.effective_status) return run.effective_status

  // Legacy fallback for API responses without effective_status
  const status = run.status?.toLowerCase() || ""
  if (status === "complete" || status === "completed") return "complete"
  if (status === "failed" || status === "error") return "failed"
  if (status === "cancelled" || status === "canceled") return "cancelled"
  if (status === "dry_run_complete") return "dry_run_complete"
  if (run.error) return "failed"
  if (status === "running" || status === "in_progress") return "running"
  return status
}

/**
 * Check if a run is actively executing (not terminal).
 * Uses getEffectiveStatus as the single source of truth.
 */
export function isRunActive(run: RunMetadataSummary): boolean {
  const status = getEffectiveStatus(run)
  return status === "running" || status === "waiting_for_review"
}

// ===== Run info extraction =====

export function getRunInfo(run: RunMetadataSummary): { name: string; secondary: string } {
  if (run.mode === "database") {
    const name = run.app_name || run.db_name || run.db_type || "Database"
    const secondary = run.db_host
      ? run.db_host.length > 40
        ? run.db_host.substring(0, 37) + "..."
        : run.db_host
      : ""
    return { name, secondary }
  } else {
    const repoMatch = run.repo_url?.match(/([^/]+)\/([^/]+?)(?:\.git)?$/)
    if (run.app_name) {
      return { name: run.app_name, secondary: repoMatch ? repoMatch[2] : "" }
    }
    if (repoMatch) {
      return { name: repoMatch[2], secondary: repoMatch[1] }
    }
    return { name: run.notes || "Repository", secondary: "" }
  }
}

// ===== Run display name (short, for sidebar) =====

export function getRunDisplayName(run: RunMetadataSummary): string {
  return run.app_name || run.notes || run.run_id
}

// ===== Date grouping =====

export function groupRunsByDate(runs: RunMetadataSummary[]): Record<string, RunMetadataSummary[]> {
  const groups: Record<string, RunMetadataSummary[]> = {}
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const thisWeekStart = new Date(today)
  thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay())
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)

  for (const run of runs) {
    // created_at is nullable — treat missing timestamps as epoch (oldest group).
    const runDate = new Date(run.created_at ?? 0)
    const runDay = new Date(runDate.getFullYear(), runDate.getMonth(), runDate.getDate())

    let group: string
    if (runDay >= today) {
      group = "Today"
    } else if (runDay >= yesterday) {
      group = "Yesterday"
    } else if (runDay >= thisWeekStart) {
      group = "This Week"
    } else if (runDay >= thisMonthStart) {
      group = "This Month"
    } else {
      group = "Older"
    }

    if (!groups[group]) {
      groups[group] = []
    }
    groups[group].push(run)
  }

  return groups
}

// ===== Time formatting =====

export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  // >7d fallback stays browser-local — relative formatter, see settings-wiring plan C3
  return date.toLocaleDateString()
}

/**
 * Compute wall-clock duration across a phase-timings map.
 *
 * Returns the span from earliest `started_at` to latest `completed_at` (in
 * seconds). This includes any time spent paused at HITL gates between
 * phases — which matches what users mean by "how long did my workflow take?"
 *
 * Returns null if we can't determine both endpoints (workflow still running,
 * no phases started, etc.).
 */
export function computeWorkflowDuration(
  phaseTimings: Record<string, { started_at?: string | null; completed_at?: string | null }> | null | undefined,
): number | null {
  if (!phaseTimings) return null
  let earliestStart: number | null = null
  let latestEnd: number | null = null
  for (const timing of Object.values(phaseTimings)) {
    if (timing.started_at) {
      const t = new Date(timing.started_at).getTime()
      if (!Number.isNaN(t) && (earliestStart === null || t < earliestStart)) earliestStart = t
    }
    if (timing.completed_at) {
      const t = new Date(timing.completed_at).getTime()
      if (!Number.isNaN(t) && (latestEnd === null || t > latestEnd)) latestEnd = t
    }
  }
  if (earliestStart === null || latestEnd === null) return null
  return Math.max(0, Math.round((latestEnd - earliestStart) / 1000))
}

/**
 * Format a duration in seconds as a short human string.
 * Examples: "42s", "3m 12s", "1h 7m", "2d 4h".
 */
export function formatDuration(totalSeconds: number | null | undefined): string | null {
  if (totalSeconds == null || totalSeconds < 0) return null
  const s = Math.round(totalSeconds)
  if (s < 60) return `${s}s`
  const mins = Math.floor(s / 60)
  if (mins < 60) {
    const rem = s % 60
    return rem > 0 ? `${mins}m ${rem}s` : `${mins}m`
  }
  const hours = Math.floor(mins / 60)
  if (hours < 24) {
    const remMins = mins % 60
    return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`
  }
  const days = Math.floor(hours / 24)
  const remHours = hours % 24
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`
}

// ===== Status badge for Jobs page =====

export function getStatusBadge(status: string): { label: string; className: string } {
  switch (status) {
    case "complete":
    case "dry_run_complete":
      return { label: "Completed", className: "text-green-600 bg-green-500/10 dark:text-green-400" }
    case "failed":
      return { label: "Failed", className: "text-red-600 bg-red-500/10 dark:text-red-400" }
    case "cancelled":
      return { label: "Cancelled", className: "text-yellow-600 bg-yellow-500/10 dark:text-yellow-400" }
    case "waiting_for_review":
      return { label: "Awaiting Review", className: "text-amber-600 bg-amber-500/10 dark:text-amber-400" }
    case "running":
      return { label: "Running", className: "text-blue-600 bg-blue-500/10 dark:text-blue-400" }
    default:
      return { label: status || "Pending", className: "text-muted-foreground bg-muted" }
  }
}
